/* global document: true */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { isEmpty } from 'underscore';

import useMap from './useMap';
// import '../../pages/Login/login.scss';

const ScorchMap = ({ circleAreas, clusterConfig, clusterMarkers, hideCircleAreas, hideMarkers, map, mapConfig, markers, onMapLoaded }) => {
    const { options, ...rest } = mapConfig || {};
    const [_map, setMap] = useState(null);
    const [_maps, setMaps] = useState(null);
    const [mapRef] = useMap(map);
    const { setCircleAreas, setMaps: setInternalMaps, setMarkers, setClusterer } = mapRef.getInternalHooks('INTERNAL_HOOK_KEY');

    useEffect(() => {
        if (!_map || !_maps) {
            return;
        }

        const makeInfoWindow = (marker) => {
            const { id, infoWindow: infoWindowConfig } = marker;
            const { onClick, onMouseOver, onMouseOut, ...config } = infoWindowConfig;
            const infoWindow = new _maps.InfoWindow({ ...config });

            let timeout = null;

            infoWindow.openWindow = () => {
                if (timeout) {
                    clearTimeout(timeout);
                }

                infoWindow.open(_map, marker);
            };

            infoWindow.closeWindow = (delay) => {
                if (delay && delay > 0) {
                    if (timeout) {
                        clearTimeout(timeout);
                    }

                    timeout = setTimeout(() => infoWindow.close(_map), delay);
                } else {
                    infoWindow.close(_map);
                }
            };

            infoWindow.addListener('domready', () => {
                const container = document.getElementById(`info-window-${id}`).closest('div[role="dialog"]');

                if (onClick) {
                    container.addEventListener('click', () => onClick(infoWindow));
                }

                container.addEventListener('mouseover', () => {
                    if (timeout) {
                        clearTimeout(timeout);
                    }

                    if (onMouseOver) {
                        onMouseOver(infoWindow);
                    }
                });

                container.addEventListener('mouseleave', () => {
                    if (onMouseOut) {
                        onMouseOut(infoWindow);
                    } else {
                        infoWindow.closeWindow();
                    }
                });
            });

            return infoWindow;
        };

        const makeMarker = (marker) => {
            const { onClick, onMouseOver, onMouseOut, ...markerConfig } = marker;

            const mappedMarker = new _maps.Marker({ _map, ...markerConfig });

            if (mappedMarker.infoWindow) {
                mappedMarker.infoWindow = makeInfoWindow(mappedMarker);
            }

            if (onClick) {
                mappedMarker.addListener('click', () => onClick(mappedMarker));
            }

            if (onMouseOver) {
                mappedMarker.addListener('mouseover', () => onMouseOver(mappedMarker));
            }

            if (onMouseOut) {
                mappedMarker.addListener('mouseout', () => onMouseOut(mappedMarker));
            }

            return mappedMarker;
        };

        if (!isEmpty(circleAreas)) {
            const mappedCircles = circleAreas.map((circle) => {
                const mappedCircle = new _maps.Circle({ _map, visible: !hideCircleAreas, ...circle });
                mappedCircle.setMap(_map);

                return mappedCircle;
            });
            setCircleAreas(mappedCircles);
        }

        if (!isEmpty(markers)) {
            const mappedMarkers = markers.map((marker) => {
                const tmpMarker = marker;
                tmpMarker.visible = !hideMarkers;

                return makeMarker(tmpMarker);
            });

            if (clusterMarkers && !hideMarkers) {
                setClusterer(new MarkerClusterer(_map, mappedMarkers, clusterConfig));
            } else {
                mappedMarkers.forEach((marker) => marker.setMap(_map));
            }

            setMarkers(mappedMarkers);
        }

    }, [_map, _maps, circleAreas, clusterConfig, hideCircleAreas, hideMarkers, markers, setCircleAreas, setClusterer, setMarkers]);

    // eslint-disable-next-line no-shadow
    const onGoogleApiLoaded = (map, maps) => {
        setMap(map);
        setMaps(maps);
        setInternalMaps(map, maps);

        if (onMapLoaded) {
            onMapLoaded(map, maps);
        }
    };

    return (
        <div className="map" style={{ height: mapConfig?.height, width: mapConfig?.width }}>
            <GoogleMapReact
                options={options}
                bootstrapURLKeys={{ libraries: mapConfig?.libraries, key: mapConfig?.key }}
                yesIWantToUseGoogleMapApiInternals
                defaultCenter={mapConfig?.defaultCenter}
                defaultZoom={mapConfig?.defaultZoom}
                onGoogleApiLoaded={({ map, maps }) => onGoogleApiLoaded(map, maps)}
                {...rest} />
        </div>
    );
};

Map.propTypes = {
    circleAreas: PropTypes.array,
    clusterConfig: PropTypes.object,
    clusterMarkers: PropTypes.bool,
    hideCircleAreas: PropTypes.bool,
    hideMarkers: PropTypes.bool,
    map: PropTypes.object,
    mapConfig: PropTypes.shape({
        defaultCenter: PropTypes.exact({
            lat: PropTypes.number,
            lng: PropTypes.number,
        }),
        defaultZoom: PropTypes.number,
        height: PropTypes.string,
        key: PropTypes.string,
        libraries: PropTypes.string,
        options: PropTypes.object,
        width: PropTypes.string,
    }).isRequired,
    markers: PropTypes.array,
    onMapLoaded: PropTypes.func,
};

Map.defaultProps = {
    circleAreas: [],
    clusterConfig: {},
    clusterMarkers: false,
    hideCircleAreas: false,
    hideMarkers: false,
    map: null,
    markers: [],
    onMapLoaded: () => { },
};

export default ScorchMap;
